import { useMemo, useState } from 'preact/hooks';
import { evaluateCountry } from '@voice-assistant/shared';
import { getBrand } from '../brands';
import { recordGeo, type GeoCheckResult } from '../api/settings';

interface Props {
  /** Called once the server records an allowed (Tier-1, or Tier-2 with a valid VAT ID) country. */
  onPassed: () => void;
}

/**
 * Protofac signup geo/VAT gate (signup-gate Phase 4, frontend half).
 *
 * Lightweight gated form — deliberately NOT the full Zustand-FSM pattern: the gate is a one-time,
 * linear, SERVER-AUTHORITATIVE step (the form only previews the shared evaluator; `POST /geo/check`
 * is the enforcement point) with no cross-device reconciliation or persistence. See the signup-gate
 * SE suite + project memory for the documented exception rationale.
 *
 * Flow: pick country → live tier preview (Tier-1 open / Tier-2 B2B-VAT / Tier-3 blocked+waitlist)
 *   → submit to the authoritative server check → allowed dismisses the gate; blocked shows a
 *   waitlist confirmation.
 */

// Full ISO-3166-1 alpha-2 list so a user from ANY country can declare it (Tier-3 → waitlist with
// their real country for analytics). Labels resolved at render via Intl.DisplayNames.
const COUNTRY_CODES = [
  'AD','AE','AF','AG','AI','AL','AM','AO','AR','AT','AU','AZ','BA','BB','BD','BE','BF','BG','BH','BI',
  'BJ','BN','BO','BR','BS','BT','BW','BY','BZ','CA','CD','CF','CG','CH','CI','CL','CM','CN','CO','CR',
  'CU','CV','CY','CZ','DE','DJ','DK','DM','DO','DZ','EC','EE','EG','ER','ES','ET','FI','FJ','FM','FR',
  'GA','GB','GD','GE','GH','GM','GN','GQ','GR','GT','GW','GY','HN','HR','HT','HU','ID','IE','IL','IN',
  'IQ','IR','IS','IT','JM','JO','JP','KE','KG','KH','KI','KM','KN','KP','KR','KW','KZ','LA','LB','LC',
  'LI','LK','LR','LS','LT','LU','LV','LY','MA','MC','MD','ME','MG','MH','MK','ML','MM','MN','MR','MT',
  'MU','MV','MW','MX','MY','MZ','NA','NE','NG','NI','NL','NO','NP','NR','NZ','OM','PA','PE','PG','PH',
  'PK','PL','PT','PW','PY','QA','RO','RS','RU','RW','SA','SB','SC','SD','SE','SG','SI','SK','SL','SM',
  'SN','SO','SR','SS','ST','SV','SY','SZ','TD','TG','TH','TJ','TL','TM','TN','TO','TR','TT','TV','TW',
  'TZ','UA','UG','US','UY','UZ','VC','VE','VN','VU','WS','YE','ZA','ZM','ZW',
];

const pStyle = { margin: '0 0 16px', fontSize: '0.9rem', lineHeight: 1.5 } as const;
const noteStyle = { margin: '12px 0 0', fontSize: '0.82rem', lineHeight: 1.4 } as const;
const selectStyle = {
  width: '100%', padding: '11px 12px', borderRadius: '8px', fontSize: '16px',
  background: 'var(--bg, #181825)', color: 'var(--fg, #cdd6f4)', border: '1px solid var(--border, #313244)',
} as const;
const inputStyle = { ...selectStyle, marginTop: '8px' } as const;
const buttonStyle = (enabled: boolean) => ({
  width: '100%', marginTop: '22px', padding: '12px', borderRadius: '8px', border: 'none',
  background: enabled ? 'var(--accent, #89b4fa)' : 'var(--border, #313244)',
  color: enabled ? '#1e1e2e' : 'var(--fg-muted, #a6adc8)',
  fontWeight: 700, fontSize: '0.9rem', cursor: enabled ? 'pointer' : 'not-allowed', transition: 'background 0.15s',
} as const);

export function GeoGate({ onPassed }: Props) {
  const [country, setCountry] = useState('');
  const [vatId, setVatId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<GeoCheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const brandName = getBrand().name;

  const options = useMemo(() => {
    let display: Intl.DisplayNames | null = null;
    try { display = new Intl.DisplayNames(['en'], { type: 'region' }); } catch { display = null; }
    return COUNTRY_CODES
      .map((code) => ({ code, name: display?.of(code) ?? code }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  // Client-side PREVIEW only (server is authoritative on submit).
  const preview = country ? evaluateCountry(country) : null;
  const needsVat = preview?.requiresVatId === true;
  const isBlockedPreview = preview != null && !preview.allowed;
  const canSubmit = !!country && (!needsVat || vatId.trim().length > 0) && !submitting;

  async function handleSubmit() {
    if (!country || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await recordGeo(country, needsVat ? vatId.trim() : undefined);
      setResult(res);
      if (!res.blocked) onPassed();
    } catch {
      setError('Could not verify your country. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // After a blocked server result — waitlist confirmation (terminal for this gate).
  if (result?.blocked) {
    const vatRejected = result.tier === 2; // Tier-2 but blocked → B2C / invalid VAT
    return (
      <Shell title={`${brandName} isn't available to you yet`}>
        <p style={pStyle}>
          {vatRejected
            ? `${brandName} is offered to businesses in your region. We couldn't validate a VAT ID for your account, so we've added you to the waitlist and will reach out as access expands.`
            : `${brandName} isn't open in your region yet. We've added you to the waitlist and will email you the moment it becomes available.`}
        </p>
        <p style={{ ...pStyle, color: 'var(--fg-muted, #a6adc8)', fontSize: '0.8rem' }}>
          You can close this page — no further action is needed.
        </p>
      </Shell>
    );
  }

  return (
    <Shell title={`Welcome to ${brandName}`}>
      <p style={pStyle}>Tell us where you're signing up from so we can set up your account correctly.</p>

      <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--fg-muted, #a6adc8)', marginBottom: '6px' }}>
        Country
      </label>
      <select
        value={country}
        onChange={(e) => { setCountry((e.target as HTMLSelectElement).value); setVatId(''); setError(null); }}
        style={selectStyle}
      >
        <option value="">Select your country…</option>
        {options.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
      </select>

      {preview?.tier === 1 && (
        <p style={{ ...noteStyle, color: 'var(--green, #a6e3a1)' }}>✓ {brandName} is available in your region.</p>
      )}

      {needsVat && (
        <div style={{ marginTop: '14px' }}>
          <p style={{ ...noteStyle, color: 'var(--fg-muted, #a6adc8)' }}>
            In your region {brandName} is offered to businesses only. Enter your VAT ID — we validate it before you can proceed.
          </p>
          <input
            type="text"
            value={vatId}
            onInput={(e) => setVatId((e.target as HTMLInputElement).value)}
            placeholder="e.g. DE123456789"
            autocomplete="off"
            spellcheck={false}
            style={inputStyle}
          />
        </div>
      )}

      {isBlockedPreview && (
        <p style={{ ...noteStyle, color: 'var(--amber, #f9e2af)' }}>
          {brandName} isn't open in your region yet — continuing will add you to the waitlist.
        </p>
      )}

      {error && <p style={{ ...noteStyle, color: 'var(--red, #f38ba8)' }}>{error}</p>}

      <button onClick={handleSubmit} disabled={!canSubmit} style={buttonStyle(canSubmit)}>
        {submitting ? 'Checking…' : isBlockedPreview ? 'Join the waitlist' : 'Continue'}
      </button>
    </Shell>
  );
}

function Shell({ title, children }: { title: string; children: preact.ComponentChildren }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px',
    }}>
      <div style={{
        background: 'var(--bg-card, #1e1e2e)', border: '1px solid var(--border, #313244)',
        borderRadius: '12px', maxWidth: '480px', width: '100%', padding: '28px 24px',
        color: 'var(--fg, #cdd6f4)', maxHeight: '90svh', overflowY: 'auto',
      }}>
        <h2 style={{ margin: '0 0 12px', fontSize: '1.2rem', fontWeight: 700 }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}
